import React from 'react'

import { Box, SelectOption, Title } from '@factorialco/deprecated-design-system'
import { FormikProps } from 'formik'
import {
  FinancialDocumentExpenseFragment,
  FinancialDocumentProjectFragment,
  FinancialDocumentPurchaseOrderFragment,
  FinancialDocumentSubprojectFragment,
  VendorSoftwareSetupFragment,
} from 'generated/resources/graphql'

import Select from 'modules/core/components/Select'
import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import ExpenseDetails from 'modules/finance/components/Invoices/Detail/Content/Form/CategorizationTab/_ExpenseDetails'
import ProjectsSelector from 'modules/finance/components/Invoices/Detail/Content/Form/CategorizationTab/_ProjectsSelector'
import PurchaseOrderSelector from 'modules/finance/components/Invoices/Detail/Content/Form/CategorizationTab/_PurchaseOrderSelector'
import SoftwareSelector from 'modules/finance/components/Invoices/Detail/Content/Form/CategorizationTab/_SoftwareSelector'
import { DisabledFields } from 'modules/finance/components/Invoices/Detail/Content/Form/_utils'
import { InvoiceForm } from 'modules/finance/components/Invoices/Detail/Content/Form/types'

interface ResourceSelectorProps {
  form: FormikProps<InvoiceForm>
  disabledFields: DisabledFields
  showSoftware: boolean
  showPurchaseOrder: boolean
  showProjects?: boolean
  selectedPurchaseOrderObject: FinancialDocumentPurchaseOrderFragment | undefined | null
  selectedSoftwareObject: VendorSoftwareSetupFragment | undefined | null
  selectedProjectObject: FinancialDocumentProjectFragment | undefined | null
  selectedSubprojectObject: FinancialDocumentSubprojectFragment | undefined | null
}

const ResourceSelector = ({
  form,
  disabledFields,
  showSoftware,
  showPurchaseOrder,
  showProjects,
  selectedPurchaseOrderObject,
  selectedSoftwareObject,
  selectedProjectObject,
  selectedSubprojectObject,
}: ResourceSelectorProps) => {
  const selectCorrectService = (selectedService: string | undefined) => {
    if (selectedService == 'subproject') {
      return 'project'
    }
    return selectedService
  }

  const [selectedService, setSelectedService] = React.useState<string | undefined>(
    selectCorrectService(form.values.associations?.[0]?.resource?.type)
  )

  const serviceOptions: SelectOption[] = []

  if (showSoftware) {
    serviceOptions.push({
      label: i18n.t('finance.invoices.detail.associated_resource_software_type'),
      value: 'softwaresetup',
    })
  }
  if (showPurchaseOrder) {
    serviceOptions.push({
      label: i18n.t('finance.invoices.detail.associated_resource_purchase_order_type'),
      value: 'purchaseorder',
    })
    if (showProjects) {
      serviceOptions.push({
        label: i18n.t('project_management.meta_data.projects_list_page.title'),
        value: 'project',
      })
    }
  }

  useEffect(() => {
    if (serviceOptions.length == 1 && serviceOptions[serviceOptions.length - 1]?.value) {
      setSelectedService(serviceOptions[serviceOptions.length - 1]?.value)
    }
  }, [serviceOptions.length])

  const handleOnChangeResourceType = (value: string) => {
    clearSelectedResource()
    setSelectedService(value)
  }

  const clearSelectedResource = () => {
    form.setFieldValue('associations', [])
  }
  return (
    <>
      <Box paddingTop="s24">
        <Title
          variant="400"
          title={i18n.t('finance.invoices.detail.associated_resource_title')}
          description={i18n.t('finance.invoices.detail.associated_resource_description')}
        />
      </Box>
      {serviceOptions.length > 1 && (
        <Box paddingTop="s16">
          <Select
            label={i18n.t('finance.invoices.detail.associated_resource_type_title')}
            options={serviceOptions}
            onChange={handleOnChangeResourceType}
            value={selectedService}
            none={i18n.t('common.select')}
            readonly={disabledFields?.associatedResource ?? false}
          />
        </Box>
      )}
      <Box paddingTop="s16">
        {showSoftware && selectedService == 'softwaresetup' && (
          <SoftwareSelector
            form={form}
            readonly={disabledFields?.associatedResource ?? false}
            onClear={clearSelectedResource}
            selectedSoftwareObject={selectedSoftwareObject}
          />
        )}

        {showPurchaseOrder && selectedService == 'purchaseorder' && (
          <PurchaseOrderSelector
            form={form}
            readonly={disabledFields?.associatedResource ?? false}
            onClear={clearSelectedResource}
            selectedPurchaseOrderObject={selectedPurchaseOrderObject}
          />
        )}

        {showProjects && selectedService == 'project' && (
          <ProjectsSelector
            form={form}
            initialSelectedProject={selectedProjectObject}
            initialSelectedSubproject={selectedSubprojectObject}
            onClear={clearSelectedResource}
            readOnly={disabledFields?.associatedResource ?? false}
          />
        )}
      </Box>
    </>
  )
}

interface Props {
  expense: FinancialDocumentExpenseFragment | undefined | null
  form: FormikProps<InvoiceForm>
  disabledFields: DisabledFields
  showSoftware: boolean
  showPurchaseOrder: boolean
  showExpense: boolean
  showProjects?: boolean
  selectedResourceLabel: string | undefined
  selectedProjectObject: FinancialDocumentProjectFragment | undefined | null
  selectedSubprojectObject: FinancialDocumentSubprojectFragment | undefined | null
  selectedSoftwareObject: VendorSoftwareSetupFragment | undefined | null
  selectedPurchaseOrderObject: FinancialDocumentPurchaseOrderFragment | undefined | null
}

export const CategorizationTab = ({
  expense,
  form,
  disabledFields,
  showSoftware,
  showPurchaseOrder,
  showExpense,
  showProjects,
  selectedProjectObject,
  selectedSubprojectObject,
  selectedSoftwareObject,
  selectedPurchaseOrderObject,
}: Props) => {
  return showExpense && expense ? (
    <ExpenseDetails expense={expense} />
  ) : (
    <ResourceSelector
      form={form}
      disabledFields={disabledFields}
      showSoftware={showSoftware}
      showProjects={showProjects}
      showPurchaseOrder={showPurchaseOrder}
      selectedProjectObject={selectedProjectObject}
      selectedSubprojectObject={selectedSubprojectObject}
      selectedSoftwareObject={selectedSoftwareObject}
      selectedPurchaseOrderObject={selectedPurchaseOrderObject}
    />
  )
}
